import React, { useCallback, useMemo, useState } from "react";
import {
  MDBBtn,
  MDBIcon,
  MDBInput,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBSpinner,
  MDBTextArea,
} from "mdb-react-ui-kit";
import { MfmImageUploader } from "Projects/CC_cubi_clan_project/GlobalComponents/MfmImageUploader/MfmImageUploader";
import { createIncident } from "Projects/CC_cubi_clan_project/Utils/IncidentService";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";
import "./IncidentCreateModal.css";

export default function IncidentCreateModal({ show, onClose, appCode = "GEN", onCreated }) {
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [descTouched, setDescTouched] = useState(false);
  const [priority, setPriority] = useState("MEDIUM");
  const [category, setCategory] = useState("");
  const [mediaUuids, setMediaUuids] = useState([]);

  const canSubmit = useMemo(() => title.trim().length >= 5 && description.trim().length >= 10, [title, description]);

  const handleHide = useCallback(() => { onClose?.(); }, [onClose]);

  const addMedia = (result) => {
    const uuid = result?.uuid;
    if (!uuid) return;
    setMediaUuids((prev) => (prev.includes(uuid) ? prev : [...prev, uuid]));
  };

  const removeMedia = (uuid) => {
    setMediaUuids((prev) => prev.filter((it) => it !== uuid));
  };

  const reset = () => {
    setTitle("");
    setDescription("");
    setPriority("MEDIUM");
    setCategory("");
    setMediaUuids([]);
    setTitleTouched(false);
    setDescTouched(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    try {
      const payload = {
        appCode,
        title: title.trim(),
        description: description.trim(),
        priority,
        category: category.trim() || null,
        descriptionMediaJson: mediaUuids.length ? JSON.stringify(mediaUuids) : null,
      };

      const result = await createIncident(payload);
      if (result.ok && result.json?.STATE === "SUCCESS") {
        toast.success("Incidencia creada correctamente");
        onCreated?.(result.json?.DATA || null);
        reset();
        onClose?.();
      } else {
        toast.error(result.json?.MESSAGE || "No se pudo crear la incidencia");
      }
    } catch (error) {
      toast.error("Error inesperado al crear la incidencia");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MDBModal show={show} onHide={handleHide} tabIndex="-1">
      <MDBModalDialog size="lg" centered>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>
              <MDBIcon fas icon="life-ring" className="me-2" />
              Nueva incidencia
            </MDBModalTitle>
            <MDBBtn className="btn-close" color="none" onClick={() => onClose?.()} />
          </MDBModalHeader>

          <MDBModalBody>
            <div className="inci-form-grid">
              <MDBInput
                label="Título"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => setTitleTouched(true)}
                maxLength={180}
              />
              {titleTouched && title.trim().length < 5 && (
                <small className="text-danger">Mínimo 5 caracteres ({title.trim().length}/5)</small>
              )}

              <label className="form-label mb-0">Prioridad</label>
              <select
                className="form-select"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                <option value="LOW">Baja</option>
                <option value="MEDIUM">Media</option>
                <option value="HIGH">Alta</option>
                <option value="URGENT">Urgente</option>
              </select>

              <MDBInput
                label="Categoría (opcional)"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                maxLength={60}
              />

              <div>
                <label className="form-label">Descripción</label>
                <MDBTextArea
                  rows={5}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={() => setDescTouched(true)}
                />
                {descTouched && description.trim().length < 10 && (
                  <small className="text-danger">Mínimo 10 caracteres ({description.trim().length}/10)</small>
                )}
              </div>

              <div>
                <label className="form-label">Adjuntos en la descripción (imagen o vídeo)</label>
                <div className="inci-uploaders">
                  <MfmImageUploader
                    appCode="MFM"
                    library="incidencias"
                    mediaType="image"
                    onUpload={addMedia}
                  />
                  <MfmImageUploader
                    appCode="MFM"
                    library="incidencias"
                    mediaType="video"
                    onUpload={addMedia}
                  />
                </div>
                {mediaUuids.length > 0 && (
                  <div className="inci-media-list">
                    {mediaUuids.map((uuid) => (
                      <button key={uuid} className="inci-media-item" onClick={() => removeMedia(uuid)}>
                        <MDBIcon fas icon="paperclip" className="me-1" />
                        {uuid}
                        <MDBIcon fas icon="times" className="ms-2" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </MDBModalBody>

          <MDBModalFooter>
            <MDBBtn color="secondary" outline onClick={() => onClose?.()} disabled={saving}>Cancelar</MDBBtn>
            <MDBBtn color="danger" onClick={handleSubmit} disabled={!canSubmit || saving}>
              {saving ? <MDBSpinner size="sm" className="me-2" /> : <MDBIcon fas icon="paper-plane" className="me-2" />}
              Crear incidencia
            </MDBBtn>
          </MDBModalFooter>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}
